/**
 * IPE 用户本地态统一入口：按 userId 隔离存储 + 退出清理注册表。
 *
 * 用法：
 * - 登录成功：bindIpeUserLocalOwner(userId)  —— 会丢弃旧全局 key（不迁移）
 *   必须在 setUser / setSessionVerified **之前同步调用**，避免子组件首屏 useState(loadCache) 读空
 * - 退出/会话失效：clearIpeUserLocalState()
 * - 读写用户数据：userLocal.getJson / setJson（自动带 userId 前缀）
 * - 内存 Map / IndexedDB 等：registerUserLocalCleanup(() => { ... })
 * - 旧全局 key：registerLegacyUserLocalKeys({ keys, prefixes, sessionKeys })
 * - 组件侧兜底：useUserLocalCacheState / useIpeUserLocalOwnerId（hooks/useIpeUserLocalOwnerId.js）
 */

const STORAGE_KEY_PREFIX = 'ipe:u:';

/** @type {string|null} */
let activeOwnerUserId = null;

/** @type {Set<() => void>} */
const cleanupHandlers = new Set();

/** @type {Set<(userId: string|null) => void>} */
const ownerChangeListeners = new Set();

/** @type {Set<string>} 未隔离的旧精确 key（登录时丢弃，绝不迁入当前用户） */
const legacyExactKeys = new Set();

/** @type {Set<string>} 未隔离的旧 key 前缀 */
const legacyKeyPrefixes = new Set();

/** @type {Set<string>} sessionStorage 中的旧全局 key */
const legacySessionExactKeys = new Set();

/** @type {Set<string>} sessionStorage 中的旧 key 前缀 */
const legacySessionKeyPrefixes = new Set();

function normalizeOwnerId(userId) {
  const id = String(userId ?? '').trim();
  return id || null;
}

function runCleanupHandlers() {
  cleanupHandlers.forEach((fn) => {
    try {
      fn();
    } catch (e) {
      console.warn('[ipeUserLocalState] cleanup 失败:', e);
    }
  });
}

function emitOwnerChange(userId) {
  ownerChangeListeners.forEach((fn) => {
    try {
      fn(userId);
    } catch (e) {
      console.warn('[ipeUserLocalState] ownerChange 失败:', e);
    }
  });
}

function scopedStorageKey(name) {
  const owner = activeOwnerUserId;
  if (!owner) return null;
  const keyName = String(name || '').trim();
  if (!keyName) return null;
  return `${STORAGE_KEY_PREFIX}${owner}:${keyName}`;
}

/**
 * 注册需要在登录时丢弃的旧全局 key（不迁移到当前用户）。
 * @param {{ keys?: string[], prefixes?: string[], sessionKeys?: string[], sessionPrefixes?: string[] }} spec
 */
export function registerLegacyUserLocalKeys(spec = {}) {
  (spec.keys || []).forEach((key) => {
    const k = String(key || '').trim();
    if (k) legacyExactKeys.add(k);
  });
  (spec.prefixes || []).forEach((prefix) => {
    const p = String(prefix || '').trim();
    if (p) legacyKeyPrefixes.add(p);
  });
  (spec.sessionKeys || []).forEach((key) => {
    const k = String(key || '').trim();
    if (k) legacySessionExactKeys.add(k);
  });
  (spec.sessionPrefixes || []).forEach((prefix) => {
    const p = String(prefix || '').trim();
    if (p) legacySessionKeyPrefixes.add(p);
  });
}

/**
 * 丢弃所有已注册的旧全局 key。
 * 策略：只删不迁，避免把上一账号/未知归属数据写进当前用户命名空间。
 */
export function dropAllLegacyUserLocalKeys() {
  if (legacyExactKeys.size > 0) {
    userLocal.dropLegacyKeys(...legacyExactKeys);
  }
  legacyKeyPrefixes.forEach((prefix) => {
    userLocal.dropLegacyKeyPrefix(prefix);
  });
  legacySessionExactKeys.forEach((key) => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  });
  legacySessionKeyPrefixes.forEach((prefix) => {
    try {
      const toRemove = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(prefix)) toRemove.push(key);
      }
      toRemove.forEach((key) => {
        try {
          sessionStorage.removeItem(key);
        } catch {
          /* ignore */
        }
      });
    } catch {
      /* ignore */
    }
  });
}

/**
 * 注册退出登录 / 换号时需要清理的内存态或其它副作用。
 * @param {() => void} handler
 * @returns {() => void} 取消注册
 */
export function registerUserLocalCleanup(handler) {
  if (typeof handler !== 'function') {
    return () => {};
  }
  cleanupHandlers.add(handler);
  return () => cleanupHandlers.delete(handler);
}

/**
 * 监听当前用户绑定变化（登录 bind / 退出 clear）。
 * 适合模块自行 bind 自己的命名空间。
 * @param {(userId: string|null) => void} listener
 * @returns {() => void} 取消监听
 */
export function onIpeUserLocalOwnerChange(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  ownerChangeListeners.add(listener);
  return () => ownerChangeListeners.delete(listener);
}

export function getIpeUserLocalOwnerId() {
  return activeOwnerUserId;
}

/**
 * 绑定当前登录用户。
 * 登录时先丢弃旧全局 key（不迁移），换号时再跑 cleanup 后加载新用户数据。
 * @param {string|number|null|undefined} userId
 */
export function bindIpeUserLocalOwner(userId) {
  const next = normalizeOwnerId(userId);
  if (next) {
    // 旧全局缓存归属不明：登录时直接丢弃，禁止迁入当前用户
    dropAllLegacyUserLocalKeys();
  }
  if (activeOwnerUserId === next) {
    emitOwnerChange(next);
    return;
  }
  if (activeOwnerUserId) {
    runCleanupHandlers();
  }
  activeOwnerUserId = next;
  emitOwnerChange(next);
}

/**
 * 退出登录 / 会话失效：执行全部 cleanup，并解除用户绑定。
 * 按 userId 隔离的 localStorage 默认保留，便于同一账号再次登录恢复本机历史。
 */
export function clearIpeUserLocalState() {
  runCleanupHandlers();
  dropAllLegacyUserLocalKeys();
  activeOwnerUserId = null;
  emitOwnerChange(null);
}

/**
 * 用户作用域 localStorage 读写。未 bind 时读写无效（get 返回空，set 返回 false）。
 */
export const userLocal = {
  /** @returns {string|null} 实际 localStorage key；未绑定返回 null */
  storageKey(name) {
    return scopedStorageKey(name);
  },

  getItem(name) {
    const key = scopedStorageKey(name);
    if (!key) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  /**
   * @returns {boolean} 是否写入成功
   */
  setItem(name, value) {
    const key = scopedStorageKey(name);
    if (!key) return false;
    try {
      localStorage.setItem(key, String(value));
      return true;
    } catch (e) {
      console.warn('[ipeUserLocalState] setItem 失败:', e);
      return false;
    }
  },

  removeItem(name) {
    const key = scopedStorageKey(name);
    if (!key) return;
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },

  getJson(name, fallback = null) {
    const raw = this.getItem(name);
    if (raw == null || raw === '') return fallback;
    try {
      return JSON.parse(raw);
    } catch {
      return fallback;
    }
  },

  /**
   * @returns {boolean}
   */
  setJson(name, value) {
    try {
      return this.setItem(name, JSON.stringify(value));
    } catch (e) {
      console.warn('[ipeUserLocalState] setJson 失败:', e);
      return false;
    }
  },

  /**
   * 删除未按用户隔离的旧全局 key，避免换号后被旧缓存覆盖。
   * @param {...string} keys
   */
  dropLegacyKeys(...keys) {
    keys.forEach((key) => {
      const k = String(key || '').trim();
      if (!k) return;
      try {
        localStorage.removeItem(k);
      } catch {
        /* ignore */
      }
    });
  },

  /**
   * 删除未隔离的旧 key 前缀（不会动 ipe:u: 用户作用域 key）。
   * @param {string} prefix
   */
  dropLegacyKeyPrefix(prefix) {
    const p = String(prefix || '').trim();
    if (!p) return;
    try {
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(p)) continue;
        if (key.startsWith(STORAGE_KEY_PREFIX)) continue;
        toRemove.push(key);
      }
      toRemove.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch {
          /* ignore */
        }
      });
    } catch {
      /* ignore */
    }
  },
};

/** @deprecated 测试或调试用：重置模块内部状态 */
export function __resetIpeUserLocalStateForTests() {
  cleanupHandlers.clear();
  ownerChangeListeners.clear();
  legacyExactKeys.clear();
  legacyKeyPrefixes.clear();
  legacySessionExactKeys.clear();
  legacySessionKeyPrefixes.clear();
  activeOwnerUserId = null;
}
